// The Chargebee JS library has to be loaded from Chargebee serveres
// See: https://www.chargebee.com/checkout-portal-docs/api.html
// This library is a workaround to inject Chargebee in the HTML body directly.
// You can use is as a decorator in React class components or as wrapper for React function components

import React from "react";
import {useAsyncResource} from "use-async-resource";

declare global {
  interface Window {
    Chargebee: any;
  }
}

// Variable to save the latest site that has been initialized
let latestSite: string | undefined;

export function getChargebeeInstance(site: string) {
  const {Chargebee} = window;
  // Unfortunately, window.Chargebee saves the first site you have "init"ed
  // Calling again the init method does not seem to have any effect
  // Here we replace the site internally in a very ugly way but,
  // since this is a scenario (changing the site) that only happens to developers
  // this solution is just enough. Also, reloading the page avoids site-changing
  if (latestSite !== site) {
    Chargebee.init({site});
    if (latestSite !== undefined) {
      const instance = Chargebee.getInstance();
      instance.site = site;
      instance.options.site = site;
    }
    latestSite = site;
  }
  return Chargebee.getInstance();
}

// https://www.chargebee.com/checkout-portal-docs/api.html
export async function addChargebeeScript(): Promise<void> {
  if (document.getElementById("chargebee-script")) return;

  const script = document.createElement("script");
  script.src = "https://js.chargebee.com/v2/chargebee.js";
  script.async = true;
  script.id = "chargebee-script";
  const scriptLoadPromise = new Promise<void>((resolve) => {
    script.onload = () => resolve();
  });
  document.body.appendChild(script);
  return scriptLoadPromise;
}

// HOC that makes sure Chargebee is loaded into the document before rendering the wrapped component
// and that is compatible with React Suspense
// https://reactjs.org/docs/higher-order-components.html
export function chargebeeWrapper(WrappedComponent: any) {
  // React function component
  return function ChargebeeHOC(props: any) {
    const [chargbeeReader] = useAsyncResource(addChargebeeScript, []);
    chargbeeReader(); // suspend component until Verovio is loaded in window.verovio
    return <WrappedComponent {...props} />;
  };
}

// Class decorator for React components (can also work as wrapper function for function components)
// https://www.typescriptlang.org/docs/handbook/decorators.html#class-decorators
// https://dev.to/phillipmalboeuf/simple-steps-to-a-typescript-class-decorator-apd
export default function loadChargebee<C>(constructor: C): C {
  // Class decorator needs to return a class definition,
  // and Typescript is not ok with us trying to return a function instead.
  // But React on the other hand accepts both ComponentClass and StatelessComponents interchangeably
  return chargebeeWrapper(constructor) as unknown as C;
}
